import { useTranslation } from 'react-i18next';
import './desk.css';

// The signed document, rendered from the run's artifact — computed values with their provenance,
// the CFIA bilingual nutrition panel (format verified against the customer's own TDS), claims as
// the gate decided them, and the signature line. Nothing here is generated; it is a VIEW of the
// artifact JSON the sign action assembled.

const DV = { sodium_mg: 2300, sugars_g: 100, calcium_mg: 1300, iron_mg: 18, potassium_mg: 3400, satfat_g: 20 };
const pct = (key, v) => (typeof v === 'number' && DV[key] ? `${Math.round((v / DV[key]) * 100)} %` : '');
const num = (v, d = 1) => (typeof v === 'number' ? Number(v.toFixed(d)) : null);

export function NftPanel({ artifact }) {
  const v = artifact?.values || {};
  const g = (k) => v[k]?.per_serving ?? null;
  const inc = (k) => (v[k]?.incomplete?.length ?? 0) > 0;
  const servingMl = artifact?.intake?.serving_ml;
  const satPlusTrans = g('satfat_g') === null ? null : g('satfat_g') + (g('transfat_g') ?? 0);
  const row = (label, val, unit, dvKey, opts = {}) => (
    <div className={`nf-r ${opts.first ? 'nf-first' : ''} ${opts.indent ? 'nf-ind' : ''}`}>
      <span>{opts.bold ? <b>{label}</b> : label} {val === null ? '—' : `${num(val)} ${unit}`}{opts.dagger ? ' †' : ''}</span>
      <b>{dvKey ? pct(dvKey, val) : ''}</b>
    </div>
  );
  return (
    <div className="nft-panel" data-testid="desk-nft">
      <div className="nf-h1">Nutrition Facts</div>
      <div className="nf-h1b">Valeur nutritive</div>
      <div className="nf-serv">Per {servingMl} mL<br />par {servingMl} mL</div>
      <div className="nf-rule" />
      <div className="nf-cal">
        <span className="nf-c">Calories {num(g('kcal'), 0) ?? '—'}</span>
        <span className="nf-dv">% Daily Value*<br />% valeur quotidienne*</span>
      </div>
      <div className="nf-rule-mid" />
      {row('Fat / Lipides', g('fat_g'), 'g', null, { first: true, bold: true })}
      <div className="nf-r nf-ind">
        <span>Saturated / saturés {num(g('satfat_g')) ?? '—'} g<br />+ Trans / trans {inc('transfat_g') ? '—' : `${num(g('transfat_g'))} g`}{inc('transfat_g') ? ' †' : ''}</span>
        <b>{satPlusTrans === null ? '' : `${Math.round((satPlusTrans / DV.satfat_g) * 100)} %`}</b>
      </div>
      {row('Carbohydrate / Glucides', g('carb_g'), 'g', null, { bold: true })}
      {row('Fibre / Fibres', g('fibre_g'), 'g', null, { indent: true })}
      {row('Sugars / Sucres', g('sugars_g'), 'g', 'sugars_g', { indent: true, dagger: inc('sugars_g') })}
      {row('Protein / Protéines', g('protein_g'), 'g', null, { bold: true })}
      {row('Cholesterol / Cholestérol', g('chol_mg'), 'mg', null, { bold: true })}
      {row('Sodium', g('sodium_mg'), 'mg', 'sodium_mg', { bold: true })}
      <div className="nf-rule" />
      {row('Potassium', g('potassium_mg'), 'mg', 'potassium_mg', { first: true })}
      {row('Calcium', g('calcium_mg'), 'mg', 'calcium_mg')}
      {row('Iron / Fer', g('iron_mg'), 'mg', 'iron_mg')}
      <div className="nf-fn">
        * 5% or less is <b>a little</b>, 15% or more is <b>a lot</b><br />
        * 5% ou moins c'est <b>peu</b>, 15% ou plus c'est <b>beaucoup</b>
      </div>
    </div>
  );
}

export function ProvenanceBlock({ artifact }) {
  const { t } = useTranslation();
  const prov = artifact?.field_provenance || {};
  const changed = Object.entries(prov)
    .filter(([k, v]) => v && v.extracted != null && String(artifact?.intake?.[k] ?? '') !== String(v.extracted))
    .map(([k, v]) => ({ key: k, was: v.extracted, now: artifact?.intake?.[k] ?? null, snippet: v.snippet }));
  if (!changed.length) return null;
  return (
    <div data-testid="desk-provenance">
      <h3>{t('desk.paper.provTitle')}</h3>
      <p style={{ fontSize: 11 }}>{t('desk.paper.provNote')}</p>
      <table><tbody>
        <tr><th>{t('desk.paper.provField')}</th><th>{t('desk.paper.provWas')}</th><th>{t('desk.paper.provNow')}</th></tr>
        {changed.map((c) => (
          <tr key={c.key}>
            <td>{c.key}{c.snippet ? <div style={{ fontSize: 10, color: '#777' }}>{String(c.snippet).slice(0, 90)}</div> : null}</td>
            <td style={{ color: '#a15c00' }}>{String(c.was)}</td>
            <td><b>{String(c.now ?? '\u2014')}</b></td>
          </tr>
        ))}
      </tbody></table>
    </div>
  );
}

export default function DeskPaper({ artifact, draft = false }) {
  const { t } = useTranslation();
  if (!artifact) return null;
  const s = artifact.sections || {};
  const intake = artifact.intake || {};
  const findings = artifact.findings || [];
  const fop = findings.find((f) => f.item_key === 'fop');
  const claimFindings = findings.filter((f) => /^claims_/.test(f.item_key));
  const incompletes = artifact.incompletes || [];
  const allergens = (s.allergens || []).map((a) => a.name).join(', ');

  return (
    <div className="desk-paper" data-testid="desk-paper">
      {(draft || artifact.draft) && (
        <div className="desk-draft-band" data-testid="desk-draft-band">{t('desk.paper.draftBand')}</div>
      )}
      <div className="dp-head">
        <h1>{String(intake.product_name || '').toUpperCase() || 'PRODUCT'}</h1>
        <div className="dp-t2">TECHNICAL DATA SHEET (TDS) / FICHE TECHNIQUE</div>
      </div>
      <div className="dp-body">
        <h3>1 · Product / Produit</h3>
        <table className="dp-kv"><tbody>
          <tr><td>Product / Produit</td><td>{intake.product_name}</td></tr>
          <tr><td>Market / Marché</td><td>{intake.market}</td></tr>
          <tr><td>Pack / Format</td><td>{intake.pack_format}</td></tr>
          <tr><td>Shelf life / Durée</td><td>{intake.shelf_life}</td></tr>
          <tr><td>pH</td><td>{intake.ph_target}</td></tr>
        </tbody></table>

        <h3>2 · Ingredients / Ingrédients</h3>
        <p>
          {s.ingredient_statement_en}<br />
          {s.ingredient_statement_fr}
          <span className="dp-prov"> — {t('desk.paper.ingredientsProv')}</span>
        </p>

        <h3>3 · Allergens / Allergènes</h3>
        <p>CONTAINS / CONTIENT: <b>{allergens || '—'}</b></p>

        <h3>4 · Nutrition Facts</h3>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <NftPanel artifact={artifact} />
          <div style={{ fontSize: 11, color: '#555', maxWidth: 260 }}>
            {t('desk.paper.valuesProv')}
            {incompletes.length > 0 && (
              <p style={{ marginTop: 8 }}>
                † {t('desk.paper.incompleteNote', { count: incompletes.length })}
              </p>
            )}
          </div>
        </div>

        <h3>6 · Claims / Allégations</h3>
        {claimFindings.length === 0 && <p>—</p>}
        {claimFindings.map((f) => (
          <p key={f.item_key} style={{ margin: '0 0 4px' }}>
            {f.status === 'ok' && <>✓ {f.message}</>}
            {f.status === 'warn' && <>⚠ {f.message}</>}
            {f.status === 'block' && <span className="dp-strike">{f.message}</span>}
            {f.status === 'unchecked' && <><span className="dp-inc">UNCHECKED</span> {f.message}</>}
            {f.citations?.[0] && <span className="dp-prov"> — {f.citations[0].source}</span>}
          </p>
        ))}

        {(artifact.intake?.extras || []).length > 0 && (
          <>
            <h3>{t('desk.paper.extras')}</h3>
            <table><tbody>
              {artifact.intake.extras.map((x, i) => (
                <tr key={i}><td style={{ width: '34%', fontWeight: 700 }}>{x.label}</td><td>{x.value}</td></tr>
              ))}
            </tbody></table>
          </>
        )}
        <h3>7 · Front-of-package / Symbole</h3>
        <p>{fop ? fop.message : '—'}{fop?.citations?.[0] && <span className="dp-prov"> — {fop.citations[0].source}</span>}</p>
      </div>
      <div className="dp-foot">{t('desk.paper.footer')}</div>
      <ProvenanceBlock artifact={artifact} />
      <div className="dp-sig">
        {t('desk.paper.signedLine', { date: artifact.signed_at ? new Date(artifact.signed_at).toLocaleString() : '' })}
      </div>
    </div>
  );
}

export function ReviewPaper({ artifact }) {
  const { t } = useTranslation();
  if (!artifact) return null;
  const findings = artifact.findings || [];
  const claims = artifact.claims || [];
  const tone = (st) => (st === 'ok' ? '#f0f9f2' : st === 'warn' ? '#fdf8ea' : st === 'unchecked' ? '#f4f4f4' : '#fdf0ef');
  return (
    <div className="desk-paper" data-testid="desk-review-paper" style={{ direction: 'inherit', textAlign: 'inherit', fontFamily: 'inherit' }}>
      <div className="dp-head"><h1 style={{ letterSpacing: 0 }}>{t('desk.review.paperTitle')}</h1>
        <div className="dp-t2">{t('desk.review.paperSub', { n: findings.length, c: claims.length })}</div></div>
      <div className="dp-body">
        <h3>{t('desk.review.verdictTable')}</h3>
        <table><tbody>
          <tr><th>{t('desk.review.colFinding')}</th><th style={{ width: 90 }}>{t('desk.review.colStatus')}</th><th style={{ width: 120 }}>{t('desk.review.colVote')}</th></tr>
          {findings.map((f) => (
            <tr key={f.item_key} style={{ background: tone(f.status) }}>
              <td><b>{f.title}</b>
                <div style={{ fontSize: 11, color: '#555', marginTop: 2 }}>{f.message}</div>
                {f.citations?.map((c, i) => (
                  <div key={i} style={{ fontSize: 10.5, background: '#f4f7fd', borderInlineStart: '3px solid #2c5dbb', padding: '4px 8px', marginTop: 4 }}>
                    <b style={{ color: '#2c5dbb' }}>{t('desk.run.citation')}: </b>{c.source} — {c.excerpt.slice(0, 180)}…
                  </div>
                ))}
              </td>
              <td>{t(`desk.status.${f.status}`)}</td>
              <td>{f.verdict === 'accepted' ? t('desk.review.voteOk') : f.verdict === 'fixed' ? t('desk.review.voteFix') : '—'}</td>
            </tr>
          ))}
        </tbody></table>
        {(artifact.image_readings || []).some((r) => r.kind === 'nutrition_table' && (r.rows || []).length) && (
          <>
            <h3>{t('desk.paper.imgTitle')}</h3>
            <p style={{ fontSize: 11 }}>{t('desk.paper.imgNote')}</p>
            {(artifact.image_readings || []).filter((r) => r.kind === 'nutrition_table').map((r, ri) => (
              <table key={ri}><tbody>
                <tr><th>{t('desk.review.colNutrient')}</th><th style={{ width: 100 }}>per 100</th><th style={{ width: 100 }}>{t('desk.review.colPerServing')}</th></tr>
                {(r.rows || []).map((row, i2) => (
                  <tr key={i2}><td>{row.label}</td><td>{row.per_100} {row.unit}</td><td>{row.per_serving} {row.unit}</td></tr>
                ))}
              </tbody></table>
            ))}
          </>
        )}
        {artifact.values && Object.keys(artifact.values).length > 0 && (
          <>
            <h3>{t('desk.review.computedTitle')}</h3>
            <p style={{ fontSize: 11 }}>{t('desk.review.computedNote')}</p>
            <table><tbody>
              <tr><th>{t('desk.review.colNutrient')}</th><th style={{ width: 110 }}>per 100g</th><th style={{ width: 110 }}>{t('desk.review.colPerServing')}</th></tr>
              {Object.entries(artifact.values).map(([k, v]) => (
                <tr key={k}><td>{v.label}</td><td>{v.per_100g} {v.unit}</td><td>{v.per_serving ?? '—'} {v.per_serving != null ? v.unit : ''}</td></tr>
              ))}
            </tbody></table>
            {(artifact.incompletes || []).length > 0 && (
              <p style={{ fontSize: 10.5, color: '#a15c00' }}>{t('desk.review.computedIncomplete', { n: (artifact.incompletes || []).length })}</p>
            )}
          </>
        )}
        <h3>{t('desk.review.lanesTitle')}</h3>
        <p style={{ fontSize: 11.5 }}>{t('desk.review.lanesNote')}</p>
      </div>
      <ProvenanceBlock artifact={artifact} />
      <div className="dp-sig">{t('desk.review.signedLine', { date: artifact.signed_at ? new Date(artifact.signed_at).toLocaleString() : '' })}</div>
    </div>
  );
}

export function printDoc() {
  document.body.classList.add('desk-print-doc');
  window.print();
  document.body.classList.remove('desk-print-doc');
}
export function printNft() {
  document.body.classList.add('desk-print-nft');
  window.print();
  document.body.classList.remove('desk-print-nft');
}
export function downloadWord(artifact) {
  // Word opens HTML saved as .doc. The paper's DOM is already the document — export the node
  // with inline-safe styling, no external assets, so the file survives leaving the app.
  const node = document.querySelector('.desk-paper');
  if (!node) return;
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
    body{font-family:Arial,Helvetica,sans-serif;font-size:13px;line-height:1.85;color:#1c1c1a}
    table{border-collapse:collapse;width:100%}td,th{border:1px solid #ccc;padding:5px 8px;text-align:left}
    h1{color:#2e7d32}h3{color:#2e7d32;border-bottom:1px solid #d9f2d0}
    .nft-panel{border:2px solid #000;width:300px;padding:8px 10px}
    .nf-h1,.nf-h1b{font-size:23px;font-weight:900}.nf-rule{height:5px;background:#000;margin:5px 0}
    .nf-r{display:flex;justify-content:space-between;border-top:1px solid #000;font-size:12.5px}
  </style></head><body>${node.innerHTML}</body></html>`;
  const blob = new Blob([html], { type: 'application/msword' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${(artifact?.intake?.product_name || 'document').replace(/[^\w-]+/g, '_')}_TDS.doc`;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
